import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, SpeakableSchema } from '@/components/seo/seo';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://fixtyer.co.uk';
const MEDIA_EMAIL = 'press@fixtyer.co.uk';

const faqs = [
  {
    question: 'Who should use the Fixtyer media enquiries page?',
    answer: 'Journalists, producers, researchers, podcasters, and editors can use this page to contact Fixtyer about UK pothole damage claims, council road maintenance data, DfT scorecards, and consumer road-damage stories.',
  },
  {
    question: 'What topics can Fixtyer comment on?',
    answer: 'Fixtyer can provide comment, context, and data on road condition trends, pothole repair trends, council comparisons, Highways Act 1980 claim mechanics, Section 58, and how councils respond to claims.',
  },
  {
    question: 'Can journalists use Fixtyer data and analysis?',
    answer: 'Yes. Public-sector source figures should be used under the Open Government Licence v3.0. Fixtyer analysis, framing, ratings methodology, and commentary may be quoted or referenced with credit to Fixtyer.',
  },
  {
    question: 'Can Fixtyer comment on legal outcomes?',
    answer: 'No. Fixtyer does not provide legal advice and cannot comment on the likely outcome of any individual claim. We can explain the process, public data sources, and evidence issues in general terms.',
  },
  {
    question: 'How should Fixtyer be credited?',
    answer: 'Please credit Fixtyer (fixtyer.co.uk). For data points we have analysed or compiled, use: Fixtyer analysis of Department for Transport and council data.',
  },
];

const breadcrumbs = [
  { name: 'Home', url: '/' },
  { name: 'Media Enquiries', url: '/media-enquiries' },
];

function MediaEnquiriesSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Media Enquiries',
    description: 'Media contact information for journalists covering UK pothole damage claims, council road maintenance data, DfT scorecards, and Fixtyer analysis.',
    url: `${SITE_URL}/media-enquiries`,
    mainEntity: {
      '@type': 'Organization',
      name: 'Fixtyer',
      url: SITE_URL,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'media enquiries',
        email: MEDIA_EMAIL,
        areaServed: 'GB',
        availableLanguage: 'English',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '167 - 169 Great Portland Street',
        addressLocality: 'London',
        postalCode: 'W1W 5PF',
        addressCountry: 'GB',
      },
    },
    about: [
      {
        '@type': 'Thing',
        name: 'Pothole damage claims',
      },
      {
        '@type': 'Thing',
        name: 'Department for Transport road maintenance scorecards',
      },
      {
        '@type': 'Thing',
        name: 'Council highway maintenance transparency reports',
      },
      {
        '@type': 'Thing',
        name: 'Section 58 defence',
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function MediaEnquiriesPage() {
  return (
    <div className="relative">
      <SEO
        title="Media Enquiries | UK Pothole Claims and Road Maintenance Data"
        description="Media enquiries for Fixtyer. Contact us for comment, data, and analysis on UK pothole damage claims, council road maintenance, DfT scorecards, and Section 58."
        canonical="/media-enquiries"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <FAQSchema faqs={faqs} />
      <SpeakableSchema
        url="/media-enquiries"
        headline="Media Enquiries"
        cssSelectors={['[data-speakable="summary"]']}
      />
      <MediaEnquiriesSchema />

      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50">
        <div
          className="absolute top-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="absolute inset-0 opacity-30 mt-8">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <defs>
              <pattern id="media-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-200" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#media-grid)" />
          </svg>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-20 lg:pt-24 lg:pb-24">
          <div className="flex items-center gap-2 text-sm text-brand-600 mb-6">
            <Link to="/" className="hover:text-brand-700 transition-colors">Home</Link>
            <svg className="w-4 h-4 text-surface-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-800">Media Enquiries</span>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 rounded-full text-brand-700 text-sm font-medium mb-6">
            Press and media contact
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-950 leading-tight mb-6">
            Media enquiries
          </h1>

          <div data-speakable="summary" className="text-lg sm:text-xl text-surface-700 max-w-3xl leading-relaxed mb-8 space-y-5">
            <p>
              Fixtyer is a self-service document-preparation platform. We prepare documents and organise
              customer-provided evidence; customers review, submit, manage and negotiate their own claims.
              We also analyse published government and local authority data on road condition and maintenance
              spending.
            </p>
            <p>
              Journalists are welcome to use our data, our analysis, and our commentary. This page sets out what we can
              provide, how to credit it, and how to reach us.
            </p>
          </div>

          <p className="text-base sm:text-lg text-surface-700 mb-8">
            <Link
              to="/press"
              className="font-semibold text-brand-700 hover:text-brand-800 underline decoration-brand-300 underline-offset-2"
            >
              Read our latest press releases
            </Link>
            {' '}for our most recent announcements and data-led stories.
          </p>
          <p className="text-base sm:text-lg text-surface-700 mb-8">
            For the Q3 2026 <Link to="/road-maintenance-index" className="font-semibold text-brand-700 underline">
              Fixtyer Road Maintenance Index
            </Link>, journalists can request the methodology note, regional or authority
            assets, and a rapid factual check. Please include the URL or figure you are
            checking and your deadline.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={`mailto:${MEDIA_EMAIL}?subject=Media%20enquiry%20for%20Fixtyer`}
              className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-brand-600 to-brand-700 text-white rounded-xl font-semibold hover:from-brand-700 hover:to-brand-800 transition-all shadow-lg shadow-brand-600/20"
            >
              Email Media Enquiries
            </a>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-brand-700 rounded-xl font-semibold border-2 border-brand-200 hover:border-brand-300 transition-colors"
            >
              View Council Rating Pages
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-10 items-start">
            <div className="space-y-8">
              <div className="bg-brand-50 rounded-2xl p-8 border border-brand-100">
                <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
                  What we can help with
                </h2>
                <p className="text-surface-700 leading-relaxed mb-5">
                  We can provide comment, context, and data on:
                </p>
                <ul className="space-y-3 text-surface-700">
                  <li className="flex gap-3">
                    <span className="text-brand-600 font-bold">-</span>
                    <span>Road condition and pothole-repair trends, nationally and for individual councils.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-600 font-bold">-</span>
                    <span>How any given highways authority compares against the Department for Transport's scorecards.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-600 font-bold">-</span>
                    <span>The legal mechanics of pothole damage claims, including the Highways Act 1980 and the Section 58 defence.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-600 font-bold">-</span>
                    <span>What the data means for drivers trying to recover the cost of pothole damage.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-600 font-bold">-</span>
                    <span>Trends in how councils respond to claims.</span>
                  </li>
                </ul>
                <p className="text-surface-700 leading-relaxed mt-5">
                  Our council rating pages break this down authority by authority, with the source data cited on each page.
                </p>
              </div>

              <div className="prose prose-lg max-w-none">
                <h2>Using our data and analysis</h2>
                <p>
                  There are two different things on our council pages, and they carry different permissions.
                </p>
                <p>
                  The underlying figures, including pothole counts, road-condition percentages, maintenance spend, and
                  DfT scorecard ratings, are drawn from published Department for Transport releases and councils' own
                  transparency reports. That source data is public sector information licensed under the Open Government
                  Licence v3.0, and you are free to use it under those terms. We always cite the original source on each
                  council page so you can go straight to it.
                </p>
                <p>
                  The analysis, framing, ratings methodology, and commentary are Fixtyer's own work. You're very welcome
                  to quote or reference it, and we ask that you credit it.
                </p>

                <h2>Attribution</h2>
                <p>
                  When using our analysis or our council ratings, please credit:
                </p>
                <div className="not-prose bg-surface-50 rounded-xl border border-surface-200 p-5 my-5">
                  <p className="font-display text-lg font-semibold text-brand-950">Fixtyer (fixtyer.co.uk)</p>
                </div>
                <p>
                  For data points we have analysed or compiled, the appropriate line is:
                </p>
                <div className="not-prose bg-brand-50 rounded-xl border border-brand-100 p-5 my-5">
                  <p className="font-display text-lg font-semibold text-brand-950">
                    Fixtyer analysis of Department for Transport and council data
                  </p>
                </div>
                <p>
                  Where possible, we'd be grateful for a link back to the relevant page on our site. If you're quoting a
                  specific council's figures, linking to that council's page lets your readers see the full breakdown and
                  the original sources.
                </p>

                <h2>What we can't do</h2>
                <p>To be clear about our limits:</p>
                <ul>
                  <li>We don't provide legal advice, and we can't comment on the likely outcome of any individual claim.</li>
                  <li>We can't discuss the details of any specific user's claim or case, for obvious reasons of confidentiality.</li>
                  <li>We can't speak on behalf of any council or the Department for Transport.</li>
                </ul>

                <h2>Spokesperson and comment</h2>
                <p>
                  We can provide named comment and background briefing on pothole claims, council road-maintenance
                  performance, and the data behind our ratings. If you need a quote, an explainer on methodology, or a
                  regional breakdown for a specific area, get in touch and tell us your deadline - we'll do our best to
                  work to it.
                </p>
              </div>
            </div>

            <aside className="bg-surface-50 rounded-2xl border border-surface-200 p-6 lg:sticky lg:top-24">
              <h2 className="font-display text-xl font-bold text-brand-950 mb-4">
                Media contact
              </h2>
              <div className="space-y-4 text-sm text-surface-700">
                <div>
                  <p className="font-semibold text-brand-950 mb-1">Email</p>
                  <a href={`mailto:${MEDIA_EMAIL}`} className="text-brand-600 hover:text-brand-700">
                    {MEDIA_EMAIL}
                  </a>
                </div>
                <div>
                  <p className="font-semibold text-brand-950 mb-1">Best for</p>
                  <p>Press requests, broadcast enquiries, podcast research, data-led local stories, and background briefings.</p>
                </div>
                <div>
                  <p className="font-semibold text-brand-950 mb-1">Response time</p>
                  <p>We aim to respond to journalists within one working day, and faster where a deadline requires it.</p>
                </div>
                <div>
                  <p className="font-semibold text-brand-950 mb-1">Registered office</p>
                  <p>167 - 169 Great Portland Street, London, W1W 5PF</p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="py-16 bg-surface-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-surface-200 p-8 mb-10">
            <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
              Contact
            </h2>
            <p className="text-surface-700 leading-relaxed mb-4">
              For all media and press enquiries, please email{' '}
              <a href={`mailto:${MEDIA_EMAIL}`} className="text-brand-600 hover:text-brand-700 font-semibold">
                {MEDIA_EMAIL}
              </a>{' '}
              with brief details of your enquiry, the outlet you're writing for, and your deadline. We aim to respond to
              journalists within one working day, and faster where a deadline requires it.
            </p>
            <p className="text-surface-700 leading-relaxed">
              Please use this address for press only. If you're a Fixtyer user with a question about your own claim, our{' '}
              <Link to="/support" className="text-brand-600 hover:text-brand-700 font-semibold">
                support team
              </Link>{' '}
              can help.
            </p>
          </div>

          <div className="text-center mb-10">
            <h2 className="font-display text-3xl font-bold text-brand-950 mb-3">
              Media FAQs
            </h2>
            <p className="text-surface-600">
              Short answers for journalists checking whether Fixtyer is the right contact.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-2xl border border-surface-200 p-6">
                <h3 className="font-display text-lg font-semibold text-brand-950 mb-2">
                  {faq.question}
                </h3>
                <p className="text-surface-700 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
